import { PausedBy, StageType, ZoneState } from '@/__generated__/graphql';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import * as state from '@/state';
import { Pause, PlayArrow } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useWorkflowStages } from '@/hooks/useWorkflowStages';
import { iconStyles, StageActionButton } from './styles';
import { useCallback, useState } from 'react';
import { StartZoneConfirmationDialog } from './StartZoneConfirmationDialog';
import { useWorkFlowTimeAndStateUpdater } from '@/hooks/useUpdateWorkFlowTimeAndState';

export const StageActions = () => {
  const selectedZone = useAtomValue(state.selectedZone);
  const currentStage = useAtomValue(state.currentWorkflowStage);
  const [openTimeModal, setOpenTimeModal] = useState<boolean>(false);
  const setStartTimeStamp = useSetAtom(state.startTimeStampForTimeLogs);
  const { isTransitionableToStartState, workflowUpdateHandler, updatingStatus, isFinalStage } =
    useWorkflowStages();

  const { updatedWorkflowTimeAndState } = useWorkFlowTimeAndStateUpdater();

  const closeTimeModal = useCallback(async () => {
    setOpenTimeModal(false);
    setStartTimeStamp(new Date());
    await workflowUpdateHandler(ZoneState.Start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!currentStage || !selectedZone) return <></>;

  const handleClick = async () => {
    if (selectedZone.workflowStatus?.state === ZoneState.Backlog) {
      setOpenTimeModal(true);
    } else if (selectedZone.workflowStatus?.state === ZoneState.Pause) {
      await workflowUpdateHandler(ZoneState.Start);
      if (selectedZone.workflowStatus.pausedBy === PausedBy.System)
        await updatedWorkflowTimeAndState({
          typeOfTimeLog: 'idleTime',
        });
    } else if (
      selectedZone.workflowStatus?.state === ZoneState.Start &&
      !isTransitionableToStartState
    ) {
      await workflowUpdateHandler(ZoneState.Pause);
      await updatedWorkflowTimeAndState({
        typeOfTimeLog: 'totalTime',
        pausedBy: 'USER',
      });
    } else {
      workflowUpdateHandler(isTransitionableToStartState ? ZoneState.Start : ZoneState.Pause);
    }
  };
  return (
    <>
      {!currentStage || (!selectedZone && <></>)}
      {currentStage && selectedZone && (
        <Stack direction="row" gap="1.6rem">
          {!isFinalStage && (
            <>
              <StageActionButton
                contained
                onClick={handleClick}
                disabled={updatingStatus}
                isWarning={
                  !isTransitionableToStartState ||
                  (isTransitionableToStartState &&
                    selectedZone.workflowStatus?.state === ZoneState.Pause)
                }
              >
                {!isTransitionableToStartState && (
                  <>
                    <Pause sx={{ fontSize: '1.6rem' }} />
                    <span>Pause</span>
                  </>
                )}
                {isTransitionableToStartState &&
                  selectedZone.workflowStatus?.state === ZoneState.Pause && (
                    <>
                      <PlayArrow sx={{ fontSize: '1.6rem' }} />
                      <span>Resume</span>
                    </>
                  )}
                {isTransitionableToStartState &&
                  selectedZone.workflowStatus?.state === ZoneState.Backlog && (
                    <>
                      <PlayArrow sx={{ fontSize: '1.6rem' }} />
                      <span>Start</span>
                    </>
                  )}
                {selectedZone.workflowStatus?.state === ZoneState.Review && (
                  <>
                    <PlayArrow sx={{ fontSize: '1.6rem' }} />
                    <span>Start</span>
                  </>
                )}
              </StageActionButton>
              {currentStage.stageType === StageType.Annotation &&
                selectedZone.workflowStatus?.state === ZoneState.Start && (
                  <StageActionButton
                    contained
                    onClick={() => {
                      workflowUpdateHandler(ZoneState.Review);
                      updatedWorkflowTimeAndState({
                        typeOfTimeLog: 'totalTime',
                      });
                    }}
                  >
                    <AbyssIcon iconName="arrow-right" style={iconStyles} />
                    <span>Mark it for next stage</span>
                  </StageActionButton>
                )}
              {currentStage.stageType === StageType.Review &&
                selectedZone.workflowStatus?.state === ZoneState.Start && (
                  <>
                    <StageActionButton
                      contained
                      onClick={() => {
                        workflowUpdateHandler(ZoneState.Approve);
                        updatedWorkflowTimeAndState({
                          typeOfTimeLog: 'totalTime',
                        });
                      }}
                    >
                      <AbyssIcon iconName="circle-check-alternative" style={iconStyles} />
                      <span>Approve</span>
                    </StageActionButton>
                    <StageActionButton
                      contained
                      isError
                      onClick={() => {
                        workflowUpdateHandler(ZoneState.Reject);
                        updatedWorkflowTimeAndState({
                          typeOfTimeLog: 'totalTime',
                        });
                      }}
                    >
                      <AbyssIcon iconName="circle-cancel" style={iconStyles} />
                      <span>Reject</span>
                    </StageActionButton>
                  </>
                )}
            </>
          )}
          <StartZoneConfirmationDialog
            openTimeModal={openTimeModal}
            closeTimeModal={closeTimeModal}
          ></StartZoneConfirmationDialog>
        </Stack>
      )}
    </>
  );
};
